import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useForm } from 'hooks';
import { GeneralInput } from 'components/input';
import Button from '@mui/material/Button';
import snakeCase from 'lodash/snakeCase';
import toUpper from 'lodash/toUpper';
import get from 'lodash/get';
import * as Constants from '@/redux/constants';

function DialogEdit({ open, onClose, model, id, payload = {} }) {
  const modelName = useMemo(() => model.replace(/([A-Z])/g, ' $1').trim(), [model]);
  const {
    itemInfo,
    optionsItemInfo,
    control,
    errors,
    fields,
    fieldArrays,
    registerOptions,
    isFetching,
    onSubmit,
  } = useForm({ model, id, payload });

  useEffect(() => {
    const con = toUpper(snakeCase(model));
    switch (itemInfo.type) {
      case Constants[`CREATE_${con}`].SUCCESS:
      case Constants[`UPDATE_${con}`].SUCCESS:
        onClose();
        break;
    }
  }, [itemInfo, model, onClose]);

  return (
    <Dialog
      fullWidth
      maxWidth='xl'
      open={open}
      onClose={isFetching ? undefined : onClose}
    >
      <form onSubmit={onSubmit}>
        <DialogTitle>{id ? 'Edit' : 'New'} {modelName}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {
              fields.map((row, i) => {
                const render = (field) => {
                  if (field.type === 'table') {
                    return (
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            {
                              field.columns.map((f) => f.type !== 'hidden' && (
                                <TableCell key={`form-${model}-edit-table-${i}-${f.name}`}>
                                  {f.label}
                                </TableCell>
                              ))
                            }
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            fieldArrays[field.name].fields.map((f, j) => (
                              <TableRow key={`form-${model}-edit-table-${i}-${f.id}`}>
                                {
                                  field.columns.map((c) => c.type !== 'hidden' && (
                                    <TableCell key={`form-${model}-edit-table-${i}-${c.name}-${j}`}>
                                      <GeneralInput
                                        {...c}
                                        name={`${field.name}[${j}].${c.name}`}
                                        type={c.type}
                                        label={null}
                                        control={control}
                                        registerOptions={registerOptions[`${field.name}[${j}].${c.name}`]}
                                        options={c.options || get(optionsItemInfo, `data.${c.name}`)}
                                        readOnly={isFetching}
                                        error={get(errors, `${field.name}[${j}].${c.name}`)}
                                        helper={get(errors, `${field.name}[${j}].${c.name}.message`)}
                                      />
                                    </TableCell>
                                  ))
                                }
                              </TableRow>
                            ))
                          }
                        </TableBody>
                      </Table>
                    );
                  } else {
                    const { name, type, readOnly, ...fieldProps } = field;
                    return (
                      <GeneralInput
                        key={`form-${model}-edit-input-${i}-${name}-input`}
                        name={name}
                        type={type}
                        control={control}
                        registerOptions={registerOptions[name]}
                        options={get(optionsItemInfo, `data.${name}`)}
                        readOnly={readOnly || isFetching}
                        error={get(errors, name)}
                        helper={get(errors, `${name}.message`)}
                        {...fieldProps}
                      />
                    );
                  }
                };

                if (!Array.isArray(row)) {
                  return render(row);
                } else {
                  const ret = [];
                  for (const field of row) {
                    ret.push(
                      <Grid
                        key={`form-${model}-input-${i}-${field.name}`}
                        size={{ xs: 12, md: 12 / row.length }}
                      >
                        {render(field)}
                      </Grid>
                    );
                  }
                  return ret;
                }
              })
            }
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" disabled={isFetching}>
            Cancel
          </Button>
          <Button type='submit' color="primary" disabled={isFetching}>
            Save
          </Button>
        </DialogActions>
      </form>

      {isFetching && <LinearProgress />}
    </Dialog>
  );
}

DialogEdit.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  model: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  payload: PropTypes.object,
};

export default DialogEdit;
